/**
 * Navbar — the workspace tab row.
 *
 * The tool is split into workspaces (see config/workspaces.js). Inside one, the
 * row reads: Home | that workspace's pages | its Field Guide | (i) scope note.
 * Which workspace and tab are active is derived from the route alone, so a deep
 * link highlights the same tab a click would.
 *
 * Home itself sits outside every workspace and shows no tab row.
 *
 * Order Rate stays off the row: it is configuration reached from the Monthly
 * uploads workspace, and opening it keeps Monthly highlighted.
 */

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { Link, useLocation } from 'react-router-dom';
import ScopeInfo from './ScopeInfo';
import { WORKSPACES, fieldGuidePath, resolveWorkspace } from '../../config/workspaces';
import { siopColors } from '../../config/theme';

// Keep inactive links in the original text-style treatment; only the active
// route receives the filled primary-blue treatment and glow.
const ACTIVE_GRADIENT = `linear-gradient(135deg, ${siopColors.primary} 0%, ${siopColors.blue} 50%, ${siopColors.navy} 100%)`;

const BTN = (isActive) => ({
  textTransform: 'capitalize',
  color: isActive ? '#fff' : '#404040',
  fontSize: '0.8rem',
  fontWeight: 500,
  padding: '4px 15px',
  margin: '0 2px',
  minHeight: 20,
  flexShrink: 0,
  border: 'none',
  borderRadius: '2rem',
  backgroundColor: isActive ? siopColors.primary : 'transparent',
  backgroundImage: isActive ? ACTIVE_GRADIENT : 'none',
  boxShadow: isActive ? '0 0 0 2px rgba(0, 119, 182, 0.2)' : 'none',
  '&:hover': {
    color: isActive ? '#fff' : '#404040',
    backgroundColor: isActive ? siopColors.primary : 'transparent',
    backgroundImage: isActive ? ACTIVE_GRADIENT : 'none',
    boxShadow: isActive
      ? '0 0 0 3px rgba(0, 119, 182, 0.22), 0 5px 14px rgba(0, 90, 138, 0.25)'
      : 'none',
    filter: isActive ? 'brightness(1.04)' : 'none',
  },
  ...(isActive && {
    '&:focus-visible': {
      color: '#fff',
      outline: '2px solid rgba(255, 255, 255, 0.95)',
      outlineOffset: 2,
      boxShadow: '0 0 0 4px rgba(0, 119, 182, 0.28), 0 5px 14px rgba(0, 90, 138, 0.25)',
    },
  }),
});

const GUIDE_WORKSPACE = WORKSPACES.find((w) => w.key === 'guide');

export default function Navbar() {
  const { pathname } = useLocation();
  const { workspace, tab, isFieldGuide } = resolveWorkspace(pathname);

  if (!workspace) return null;

  // A workspace's own Field Guide tab carries the guide's scope note.
  const scopeLines = isFieldGuide ? GUIDE_WORKSPACE.tabs[0].scope : tab?.scope;

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{ backgroundColor: '#fff', boxShadow: '0px 3px 6px #0000001F' }}
    >
      <Toolbar
        variant="dense"
        sx={{ overflowX: 'auto' }}
        component="nav"
        aria-label={workspace.label}
      >
        <Button
          component={Link}
          to="/home"
          startIcon={<HomeOutlinedIcon sx={{ fontSize: 18 }} />}
          sx={BTN(false)}
        >
          Home
        </Button>
        {workspace.tabs.map((t) => {
          const active = !isFieldGuide && tab === t;
          return (
            <Button
              key={t.path}
              component={Link}
              to={t.path}
              aria-current={active ? 'page' : undefined}
              sx={BTN(active)}
            >
              {t.label}
            </Button>
          );
        })}
        {workspace.hasFieldGuide && (
          <Button
            component={Link}
            to={fieldGuidePath(workspace.key)}
            aria-current={isFieldGuide ? 'page' : undefined}
            sx={BTN(isFieldGuide)}
          >
            Field Guide
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <ScopeInfo lines={scopeLines} />
      </Toolbar>
    </AppBar>
  );
}
